import { createContext, memo, useEffect, useMemo, useState } from 'react';

declare global {
  interface Window {
    analytics: {
      page: () => void;
      track: (event: string, data: Record<string, unknown>) => void;
    };
  }
}
import Cookies from 'universal-cookie';

import { isClient, isMobile } from '../lib/utils';
import {
  useRegistryContext,
  type WWQueryProps,
} from '../context/RegistryContext';
import { useIsLiteSite } from '../lib/useIsLiteSite';
import { useVisiblePages } from '../lib/useVisiblePages';

import {
  isWwsWeddingWebsiteV2,
  type Wws_Page,
  type Maybe,
  type Gds_Event,
  type Wws_PageItem,
} from '../types/graphql';

interface TrackingInfo {
  hasWWSAccommodations: boolean;
  hasWWSCoverPhoto: boolean;
  hasWWSFirstNames: boolean;
  hasWWSLastNames: boolean;
  hasWWSWeddingDate: boolean;
  hasWWSWeddingLocation: boolean;
  hasWWSRegistry: boolean;
  customDomain: boolean;
  numPagesVisible: number;
  rsvpOnCount: number;
  security: string;
  platform: string;
  product: string;
  wwsTemplateVersion: string;
  wwsPages: Maybe<string>[];
  weddingUUID?: Maybe<string> | undefined;
  ownerUUID?: Maybe<string> | undefined;
  guestUUID?: Maybe<string> | undefined;
  themeName?: Maybe<string> | undefined;
  themeId?: Maybe<number> | undefined;
}

export interface TrackingContextValue {
  trackingInfoLoaded: boolean;
  trackingInfo?: Partial<TrackingInfo> | undefined;
}

export const TrackingContext = createContext<TrackingContextValue>({
  trackingInfoLoaded: false,
});

// const TrackingConsumer = TrackingContext.Consumer;

function hasAccommodations(
  pages: Maybe<ReadonlyArray<Wws_Page>> | undefined
): boolean {
  return Boolean(
    pages?.find(({ items }: Wws_Page): boolean => {
      return Boolean(
        items?.find(
          ({ type }: Wws_PageItem): boolean => type === 'AccommodationItem'
        )
      );
    })
  );
}

function TrackingProviderComponent({ children, data }: WWQueryProps) {
  const [trackingInfo, setTrackingInfo] = useState<TrackingInfo | undefined>(
    undefined
  );

  const [trackingInfoLoaded, setTrackingInfoLoaded] = useState(false);

  const { coupleInfo } = useRegistryContext();

  const cookies = isClient() ? new Cookies() : null;

  const rsvpOnCount = useMemo<number>(() => {
    return (
      data?.events?.filter((e: Gds_Event): boolean => Boolean(e.requireRsvp))
        .length || 0
    );
  }, [data.events]);

  const isLiteSite = useIsLiteSite();

  const visiblePages = useVisiblePages();

  useEffect((): void => {
    const weddingWebsite =
      isWwsWeddingWebsiteV2(data.weddingWebsiteResult) &&
      data.weddingWebsiteResult;

    if (weddingWebsite && coupleInfo) {
      const hasWWSFirstNames =
        Boolean(weddingWebsite.firstName) &&
        Boolean(weddingWebsite.fianceFirstName);

      const hasWWSLastNames =
        Boolean(weddingWebsite.lastName) &&
        Boolean(weddingWebsite.fianceLastName);

      const ti: TrackingInfo = {
        hasWWSAccommodations: hasAccommodations(weddingWebsite.pages),
        hasWWSCoverPhoto: Boolean(weddingWebsite.coverPhoto),
        hasWWSFirstNames,
        hasWWSLastNames,
        hasWWSWeddingDate: Boolean(data.wedding?.weddingDate),
        hasWWSWeddingLocation: Boolean(weddingWebsite.weddingLocation),
        hasWWSRegistry: coupleInfo.registries?.length > 0,
        rsvpOnCount,
        security: data.guestWedding?.isPrivateRsvp ? 'private' : 'public',
        platform: isMobile() ? 'mobile' : 'web',
        guestUUID: cookies?.get('gid'),
        ownerUUID: weddingWebsite.userUuid || '',
        weddingUUID: weddingWebsite.weddingUuid || '',
        customDomain: Boolean(weddingWebsite.purchaseDomain),
        product: 'wedding websites',
        themeName: weddingWebsite.theme?.name || '',
        themeId: weddingWebsite.theme?.id || 1,
        numPagesVisible: visiblePages?.length || 0,
        wwsPages:
          visiblePages
            ?.map((page: Wws_Page): string => page.title || '')
            .filter(Boolean) || [],
        wwsTemplateVersion: isLiteSite ? 'Lite Site' : 'v2',
      };

      setTrackingInfo(ti);

      setTrackingInfoLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, coupleInfo]);

  // onload analytics page call
  useEffect((): void => {
    if (window.location.pathname.endsWith('/registry')) return;

    window.analytics.page();
  }, []);

  const value = useMemo<TrackingContextValue>(
    () => ({ trackingInfo, trackingInfoLoaded }),
    [trackingInfo, trackingInfoLoaded]
  );

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
}

export const TrackingProvider = memo(TrackingProviderComponent);
